import React, { useState } from "react"
import {
  FormContainer,
  LabeledInput,
  DatePicker,
  Worksheet,
  formatCurrency,
  CheckboxYesNo
} from "uikit"
import { Button, Input, Grid, Icon, Dropdown } from "semantic-ui-react"
import { Footer } from "./Footer"
import { AddRealEstateModal } from "components/accounts/AddRealEstateModal"
import { AddDepreciationModal } from "../../../depreciation/AddDepreciationModal"
import { useStore } from "store"

const DepreciationsTable = ({
  depreciationsList,
  removeDepreciation,
  editDepreciation,
  depreciations
}) => (
  <Grid style={{ marginTop: "1em" }}>
    {depreciationsList.map((el, i) => {
      return (
        <React.Fragment key={i}>
          {i > 0 ? <hr style={{ width: "100%" }} /> : null}
          <Grid.Row
            columns="3"
            textAlign="left"
            style={{ padding: "0px", fontSize: "0.9em" }}
          >
            <Grid.Column width="12">
              <div style={{ width: "100%", float: "left", color: "black" }}>
                {el.description}
                <span style={{ float: "right", fontWeight: "400" }}>
                  {formatCurrency(el.basis)}
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  float: "left",
                  color: "black",
                  fontWeight: "400"
                }}
              >
                {el.start_date}
                <span style={{ float: "right" }}>
                  {el.recovery_period} years
                </span>
              </div>
            </Grid.Column>
            <Grid.Column
              width="2"
              onClick={() => {
                depreciations.array
                  .find(
                    element =>
                      element.fields.description.value === el.description
                  )
                  .remove()
                removeDepreciation(i)
              }}
            >
              <Icon name="trash" fitted link style={{ paddingTop: "12px" }} />
            </Grid.Column>
            <Grid.Column
              width="2"
              onClick={() => {
                const depreciation = depreciations.array.find(
                  element => element.fields.description.value === el.description
                )
                editDepreciation(depreciation)
              }}
            >
              <Icon name="edit" fitted link style={{ paddingTop: "12px" }} />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
      )
    })}
  </Grid>
)

const RentalForm = ({
  data: {
    fields: {
      description,
      monthly_amount,
      until_date,
      hasFees,
      monthly_management_fee,
      management_fee_inflation,
      absolute_inflation,
      annual_expenses,
      depreciations,
      property_id
    },
    isModified,
    onSubmit,
    error
  },
  submitLabel = "Done",
  onDelete,
  depreciationsList,
  addNewDepreciation,
  removeDepreciation,
  changeDepreciation,
  propertyOptions,
  isPrimaryResidenceDefaultValue,
  addRealEstateProperty,
  isNew
}) => {
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [depreciation, setDepreciation] = useState(null)
  const [index, setIndex] = useState(0)
  const addModalOptions = {
    values: { is_primary_residence: isPrimaryResidenceDefaultValue }
  }

  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row
          label="What property is linked with this rental income?"
          paddedField
        >
          <Worksheet.Field error={property_id.error}>
            <Dropdown
              {...property_id}
              placeholder="Real Estate"
              options={[
                ...propertyOptions,
                { text: "Add Property...", value: "__ADD__" }
              ]}
              className="has-add-option"
              selection
              data-testid="property"
            />
            {property_id.value === "__ADD__" && (
              <AddRealEstateModal
                submit={propertyValues =>
                  addRealEstateProperty(propertyValues).then(({ propertyId }) =>
                    property_id.onChange(null, { value: propertyId })
                  )
                }
                onCancel={() => property_id.onChange(null, { value: null })}
                {...addModalOptions}
              />
            )}
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label={"Description of property"}
          paddedField
          description={`A label for this income source, like the street name.`}
        >
          <Worksheet.Field error={description.error}>
            <Input
              {...description}
              autoComplete="off"
              placeholder={
                propertyOptions.filter(p => p.value === property_id.value)
                  .length
                  ? propertyOptions.filter(
                      p => p.value === property_id.value
                    )[0].text
                  : ""
              }
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="What is the monthly gross rental income?"
          paddedField
          description="The monthly rental payment you receive before taxes and deductions"
        >
          <Worksheet.Field error={monthly_amount.error}>
            <LabeledInput
              {...monthly_amount}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="What annual rent increase would you expect to apply?"
          description="Annual inflation averages about 3%. If you expect your rental income to increase at a different rate, edit this box."
          paddedField
        >
          <Worksheet.Field error={absolute_inflation.error}>
            <LabeledInput
              {...absolute_inflation}
              label=""
              iconText="%"
              iconPosition="right"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Are there any management fees associated with this rental property?"
          description="Management fees such as condo fees or HOA fees can be deductible from rental income."
          paddedField
        >
          <Worksheet.Field>
            <CheckboxYesNo {...hasFees} style={{ textAlign: "center" }} />
          </Worksheet.Field>
        </Worksheet.Row>
        {hasFees.checked ? (
          <>
            <Worksheet.Row label="Total monthly management fees" paddedField>
              <Worksheet.Field error={monthly_management_fee.error}>
                <LabeledInput
                  {...monthly_management_fee}
                  label=""
                  iconText="$"
                  iconPosition="left"
                  placeholder="0"
                />
                {!!monthly_management_fee.value ? (
                  <span style={{ fontWeight: "normal" }}>
                    {formatCurrency(
                      12 * monthly_management_fee.value.replace(/,/g, "")
                    )}{" "}
                    per year
                  </span>
                ) : null}
              </Worksheet.Field>
            </Worksheet.Row>
            <Worksheet.Row
              label="Annual fee increase"
              description="Annual inflation averages about 3%. If you expect the management fees to increase at a different rate, edit this box."
              paddedField
            >
              <Worksheet.Field error={management_fee_inflation.error}>
                <LabeledInput
                  {...management_fee_inflation}
                  label=""
                  iconText="%"
                  iconPosition="right"
                />
              </Worksheet.Field>
            </Worksheet.Row>
          </>
        ) : null}
        <Worksheet.Row
          label="What are the remaining ANNUAL deductible expenses for this rental property?"
          paddedField
          description={`Property insurance, utilities and repairs are typically deductible. Don't include any property taxes or mortgage interest as we will automatically deduct these expenses based on the information you gave us in the "Accounts + Property" section.`}
        >
          <Worksheet.Field error={annual_expenses.error}>
            <LabeledInput
              {...annual_expenses}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Depreciation"
          description="You can deduct the costs of buying and improving a property from your taxes through the process of depreciation."
          paddedField
        >
          <Worksheet.Field>
            <DepreciationsTable
              depreciationsList={depreciationsList}
              depreciations={depreciations}
              removeDepreciation={removeDepreciation}
              editDepreciation={dep => {
                setDepreciation(dep)
                setIndex(dep.key)
                setIsEditing(true)
                setShowModal(true)
              }}
            />
            <Button
              onClick={() => {
                setIsEditing(false)
                setShowModal(true)
              }}
              style={{ marginTop: "24px" }}
              content="Add Depreciation"
              basic
              primary
              type="button"
            />

            {showModal && (
              <AddDepreciationModal
                isEditing={isEditing}
                depreciation={depreciation}
                submit={dep => {
                  if (isEditing) {
                    changeDepreciation(dep, index)
                    const keys = Object.keys(dep)
                    keys.forEach(key => {
                      setTimeout(() => {
                        depreciations.array[index].fields[key].onChange({
                          target: { value: dep[key] }
                        })
                      }, 10)
                    })
                  } else {
                    addNewDepreciation(dep)
                    depreciations.append(dep)
                  }
                  setShowModal(false)
                }}
                cancel={() => setShowModal(false)}
              />
            )}
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Until when do you plan to rent this property?"
          paddedField
        >
          <Worksheet.Field error={until_date.error}>
            <DatePicker {...until_date} />
          </Worksheet.Field>
        </Worksheet.Row>
      </Worksheet.Body>
      <Footer
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Income"
        error={error}
      />
    </Worksheet>
  )
}

const RentalFormContainer = FormContainer(
  {
    description: {
      type: "string",
      required: false
    },
    monthly_amount: {
      type: "unit"
    },
    property_id: {
      type: "string",
      required: false
    },
    until_date: {
      type: "date",
      maxValue: new Date(new Date().getFullYear() + 75, 0, 1)
    },
    is_inflation_adjusted: {
      type: "boolean",
      defaultValue: "true"
    },
    relative_inflation: {
      type: "unit",
      required: false,
      defaultValue: 0
    },
    absolute_inflation: {
      type: "unit",
      required: false,
      defaultValue: 3
    },
    annual_expenses: {
      type: "unit",
      required: false
    },
    depreciations: {
      type: "array",
      required: false,
      schema: {
        description: {
          type: "string"
        },
        basis: {
          type: "unit"
        },
        start_date: {
          type: "date"
        },
        recovery_period: {
          type: "decimal",
          defaultValue: 27.5,
          minValue: 0,
          maxValue: 40
        }
      }
    },
    hasFees: {
      type: "boolean",
      required: false,
      defaultValue: "false"
    },
    monthly_management_fee: {
      type: "unit",
      required: false
    },
    management_fee_inflation: {
      type: "unit",
      required: false,
      defaultValue: 3
    },
    type: {
      type: "string",
      defaultValue: "rental"
    }
  },
  ({ fields }, ...form) => {
    const { hasFees, monthly_management_fee } = fields
    if (hasFees.checked && monthly_management_fee.value === "") {
      return {
        fields: {
          monthly_management_fee: `This field is required`
        }
      }
    }
  }
)(RentalForm)

const Rental = ({ isNew, income, ...formProps }) => {
  const values = isNew
    ? {}
    : {
        ...income,
        absolute_inflation: income.relative_inflation + 3,
        hasFees: !!income.monthly_management_fee,
        management_fee_inflation: !!income.monthly_management_fee
          ? income.management_fee_inflation
          : 3
      }
  const options = isNew ? [] : income.depreciations
  const [depreciationsList, setDepreciationsList] = useState(options)

  const [properties, { addProperty }] = useStore(
    "householdProperties",
    formProps.household.id
  )
  const propertyOptions = Object.values(properties)
    .filter(property => property.type === "realestate")
    .map(({ description: text, id: value }) => ({
      text,
      value
    }))
  const primaryResidence = Object.values(properties).find(
    property => property.is_primary_residence
  )
  const isPrimaryResidenceDefaultValue = !primaryResidence

  const addNewDepreciation = value => {
    setDepreciationsList([...depreciationsList, value])
  }
  const removeDepreciation = index => {
    const deps = [...depreciationsList]
    deps.splice(index, 1)
    setDepreciationsList(deps)
  }
  const changeDepreciation = (value, index) => {
    const deps = [...depreciationsList]
    deps[index] = value
    setDepreciationsList(deps)
  }
  const addRealEstateProperty = propertyValues =>
    addProperty({
      household_id: formProps.household.id,
      type: "realestate",
      ...propertyValues
    })
  const submit = values => {
    const newValues = { ...values }
    newValues.is_inflation_adjusted = newValues.absolute_inflation !== 0
    newValues.relative_inflation = !newValues.is_inflation_adjusted
      ? 0
      : newValues.absolute_inflation - 3
    delete newValues["absolute_inflation"]
    if (!newValues.hasFees) {
      newValues["monthly_management_fee"] = 0
      newValues["management_fee_inflation"] = 0
    }
    delete newValues["hasFees"]
    if (!newValues.description) {
      if (newValues.property_id) {
        newValues.description = propertyOptions.filter(
          p => p.value === newValues.property_id
        )[0].text
      } else {
        newValues.description = "Real Estate"
      }
    }
    formProps.submit(newValues)
  }

  return (
    <RentalFormContainer
      {...{
        values,
        depreciationsList,
        addNewDepreciation,
        removeDepreciation,
        changeDepreciation,
        ...formProps,
        propertyOptions,
        isPrimaryResidenceDefaultValue,
        addRealEstateProperty,
        submit
      }}
    />
  )
}

export default Rental
